import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getBrandConfig } from 'src/backend/brand';
import {
  BRAND_SLUG,
  brandText,
  setRuntimeBrandName,
  setRuntimeBrandSlug,
} from 'src/utils/brand';

// Careit defaults. `null` asset/color fields mean "use the bundled defaults"
// (the SVG logos in /static/brand and the Careit palette).
export const DEFAULT_BRAND = {
  isDefault: true,
  slug: null,
  displayName: 'Careit',
  primaryColor: null,
  secondaryColor: null,
  logoUrl: null,
  logoIconUrl: null,
  faviconUrl: null,
  supportUrl: null,
  dashboardUrl: null,
  isActive: true,
};

const BrandContext = createContext({
  brand: DEFAULT_BRAND,
  loading: false,
  disabled: false,
  hasBrand: false,
});

// Point the favicon links at the brand's favicon (falls back silently if absent).
const applyFavicon = (faviconUrl) => {
  if (!faviconUrl) return;
  try {
    const links = document.querySelectorAll("link[rel~='icon']");
    if (links.length === 0) {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
      return;
    }
    links.forEach((link) => {
      link.href = faviconUrl;
    });
  } catch {
    /* no-op */
  }
};

export function BrandProvider({ children }) {
  const [hasBrand, setHasBrand] = useState(!!BRAND_SLUG);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    let active = true;
    getBrandConfig()
      .then((cfg) => {
        if (!active || !cfg) return;
        const resolved = { ...DEFAULT_BRAND, ...cfg };
        setRuntimeBrandName(resolved.displayName);
        setRuntimeBrandSlug(resolved.slug);
        setBrand(resolved);
        setHasBrand(!resolved.isDefault);
        setDisabled(cfg.isActive === false);
      })
      .catch(() => {
        /* fall back to Careit defaults */
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // Reflect the brand in the browser chrome (tab title + favicon).
  useEffect(() => {
    if (brand.isDefault) {
      // index.html ships a neutral title for link previews; Careit keeps its
      // name unless a page has already set its own title.
      if (document.title === 'Dashboard') document.title = 'Careit App';
      return;
    }
    if (brand.displayName) document.title = brand.displayName;
    applyFavicon(brand.faviconUrl);
  }, [brand]);

  const value = useMemo(
    () => ({ brand, loading, disabled, hasBrand }),
    [brand, loading, disabled, hasBrand]
  );

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
}

/** The resolved brand config (Careit defaults when unbranded). */
export const useBranding = () => useContext(BrandContext).brand;

/** Replace Careit in user-facing copy with the resolved brand display name. */
export const useBrandText = () => {
  const brand = useBranding();
  return useMemo(
    () => (value) => brandText(value, brand.displayName),
    [brand.displayName]
  );
};

/** Full brand state incl. loading/disabled flags (for the app gate). */
export const useBrandState = () => useContext(BrandContext);
